import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useTotpStatusQuery,
  useTotpDisableMutation,
  useRegenerateBackupCodesMutation,
} from '@src/features/auth/redux';
import { BackupCodesDisplay } from '@src/features/auth/components';
import { getMfaErrorCode, MFA_ERROR_CODE } from '@src/features/auth/utils/mfa-error';
import { routesConfig } from '@src/core/routes';
import { useDisclosure } from '@src/core/hooks';
import { trackError, toTrackedError } from '@src/core/sentry';
import {
  ArcAlert,
  ArcBox,
  ArcButton,
  ArcSkeleton,
  ArcStack,
  ArcText,
} from '@src/core/ui';
import TwoFactorCodeDialog from './two-factor-code-dialog';

// Profile-level 2FA management. Reads /totp/status to decide what to show:
//  - not enabled  -> offer enrollment (reuses the forced-enrollment page route).
//  - enabled      -> allow regenerating backup codes and disabling (unless mfaEnforced).
const TwoFactorSection: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: status, isLoading } = useTotpStatusQuery();

  const [disableTotp, { isLoading: isDisabling }] = useTotpDisableMutation();
  const [regenerate, { isLoading: isRegenerating }] = useRegenerateBackupCodesMutation();

  const disableDialog = useDisclosure();
  const regenerateDialog = useDisclosure();

  const [dialogError, setDialogError] = React.useState<string | null>(null);
  const [newBackupCodes, setNewBackupCodes] = React.useState<string[] | null>(null);

  if (isLoading) {
    return <ArcSkeleton height={120} />;
  }

  const totpEnabled = Boolean(status?.totpEnabled);
  const mfaEnforced = Boolean(status?.mfaEnforced);

  const onEnroll = () => navigate(routesConfig.mfaEnrollment);

  const onDisableSubmit = (code: string) => {
    setDialogError(null);
    disableTotp({ code })
      .unwrap()
      .then(() => {
        toast.success(t('mfa.profile.disabledToast'));
        disableDialog.hide();
      })
      .catch((err: unknown) => {
        const errCode = getMfaErrorCode(err);
        if (errCode === MFA_ERROR_CODE.MANDATORY) {
          setDialogError(t('mfa.errors.MFA_MANDATORY'));
          return;
        }
        setDialogError(t('mfa.errors.MFA_CODE_INVALID'));
        trackError(toTrackedError(err), { code: 'FE_0022', extra: { flow: 'totpDisable' } });
      });
  };

  const onRegenerateSubmit = (code: string) => {
    setDialogError(null);
    regenerate({ code })
      .unwrap()
      .then((response) => {
        setNewBackupCodes(response.backupCodes);
        regenerateDialog.hide();
      })
      .catch((err: unknown) => {
        setDialogError(t('mfa.errors.MFA_CODE_INVALID'));
        trackError(toTrackedError(err), {
          code: 'FE_0022',
          extra: { flow: 'regenerateBackupCodes' },
        });
      });
  };

  return (
    <ArcStack p={3} spacing={2} borderRadius={1} bgcolor='background.paper'>
      <ArcStack direction='row' alignItems='center' justifyContent='space-between'>
        <ArcText.h3 fontSize={16}>{t('mfa.profile.title')}</ArcText.h3>
        <ArcText
          fontSize={13}
          fontWeight={700}
          color={totpEnabled ? 'success.main' : 'text.secondary'}
        >
          {totpEnabled ? t('mfa.profile.statusEnabled') : t('mfa.profile.statusDisabled')}
        </ArcText>
      </ArcStack>

      {!totpEnabled && (
        <ArcStack spacing={2}>
          <ArcText fontSize={14} color='text.secondary'>
            {t('mfa.profile.enablePrompt')}
          </ArcText>
          {mfaEnforced && (
            <ArcAlert severity='warning'>
              {t('mfa.profile.enforcedAlert')}
            </ArcAlert>
          )}
          <ArcButton.brand sx={{ alignSelf: 'flex-start', minWidth: 160 }} onClick={onEnroll}>
            {t('mfa.profile.enableButton')}
          </ArcButton.brand>
        </ArcStack>
      )}

      {totpEnabled && (
        <ArcStack spacing={2}>
          {newBackupCodes ? (
            <ArcStack spacing={2}>
              <ArcText fontSize={14} fontWeight={700}>
                {t('mfa.profile.newBackupCodes')}
              </ArcText>
              <BackupCodesDisplay codes={newBackupCodes} />
              <ArcButton.outlined
                sx={{ alignSelf: 'flex-start', minWidth: 220 }}
                onClick={() => setNewBackupCodes(null)}
              >
                {t('mfa.profile.done')}
              </ArcButton.outlined>
            </ArcStack>
          ) : (
            <ArcBox>
              <ArcText fontSize={14} color='text.secondary' mb={2}>
                {t('mfa.profile.backupCodesInfo')}
              </ArcText>
              <ArcStack direction='row' spacing={2}>
                <ArcButton.outlined
                  sx={{ minWidth: 220 }}
                  onClick={() => {
                    setDialogError(null);
                    regenerateDialog.show();
                  }}
                >
                  {t('mfa.profile.regenerateButton')}
                </ArcButton.outlined>
                <ArcButton.outlined
                  sx={{ minWidth: 160 }}
                  disabled={mfaEnforced}
                  onClick={() => {
                    setDialogError(null);
                    disableDialog.show();
                  }}
                >
                  {t('mfa.profile.disableButton')}
                </ArcButton.outlined>
              </ArcStack>
              {mfaEnforced && (
                <ArcText fontSize={12} color='text.secondary' mt={1}>
                  {t('mfa.profile.enforcedHint')}
                </ArcText>
              )}
            </ArcBox>
          )}
        </ArcStack>
      )}

      <TwoFactorCodeDialog
        open={disableDialog.open}
        onClose={disableDialog.hide}
        onSubmit={onDisableSubmit}
        isSubmitting={isDisabling}
        errorMessage={dialogError}
        title={t('mfa.profile.disableDialogTitle')}
        description={t('mfa.profile.disableDialogDescription')}
        submitText={t('mfa.profile.disableDialogSubmit')}
      />

      <TwoFactorCodeDialog
        open={regenerateDialog.open}
        onClose={regenerateDialog.hide}
        onSubmit={onRegenerateSubmit}
        isSubmitting={isRegenerating}
        errorMessage={dialogError}
        title={t('mfa.profile.regenerateDialogTitle')}
        description={t('mfa.profile.regenerateDialogDescription')}
        submitText={t('mfa.profile.regenerateDialogSubmit')}
      />
    </ArcStack>
  );
};

export default TwoFactorSection;
